/**
 * @fileoverview Student invite claim page.
 *
 * Validates an invite token, shows the teacher/student info, and lets the
 * student create an account (or link to existing one) to join the portal.
 * Multi-step: Step 1 = account setup, Step 2 = musical profile.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, Loader2, Music, Star, ChevronRight, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";
import { INTEREST_OPTIONS } from "@/lib/interests";

const StudentInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  // True when a portal account already owns this email (multi-teacher student):
  // the claim links to it and requires the existing password rather than a new one.
  const [hasExistingAccount, setHasExistingAccount] = useState(false);
  // Age band drives terms wording (13-17 adds a guardian line) and the under-13
  // deflection: under-13 students don't get a portal login (the parent is served
  // by summary email), so the form is replaced by an explanatory screen.
  const [ageBand, setAgeBand] = useState(null);
  const [portalAllowed, setPortalAllowed] = useState(true);
  const [tosAccepted, setTosAccepted] = useState(false);

  // Step 1 fields
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Step 2 fields
  const [favoriteArtist, setFavoriteArtist] = useState("");
  const [interests, setInterests] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.post(
          `${API}/student/auth/invite/validate`,
          { token },
          { withCredentials: true }
        );
        setInviteInfo(res.data);
        setFirstName(res.data.student_first_name || "");
        setLastName(res.data.student_last_name || "");
        setEmail(res.data.student_email || "");
        setHasExistingAccount(Boolean(res.data.has_existing_account));
        setAgeBand(res.data.age_band || null);
        // Absent flag on an older backend defaults to allowed — only an explicit
        // false (under-13) deflects, so the change fails open for other bands.
        setPortalAllowed(res.data.portal_allowed !== false);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired invite link.");
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleStep1 = (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    // Existing-account link: only the current password is entered, no confirm.
    if (!hasExistingAccount && password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (!tosAccepted) {
      toast.error("Please accept the Terms of Service and Privacy Policy to continue");
      return;
    }
    setStep(2);
  };

  const toggleInterest = (interest) => {
    setInterests((prev) =>
      prev.includes(interest) ? prev.filter((i) => i !== interest) : [...prev, interest]
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/student/auth/invite/claim`,
        {
          token,
          email,
          first_name: firstName,
          last_name: lastName,
          password,
          favorite_artist: favoriteArtist.trim() || null,
          interests,
          tos_accepted: tosAccepted,
        },
        { withCredentials: true }
      );
      toast.success(hasExistingAccount ? "Welcome back!" : "Welcome to Hans!");
      navigate("/student/dashboard", { state: { student: res.data.account } });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to create account"));
      // A 409 means the existing password didn't match — send them back to the
      // password step to try again (or reset it).
      if (err.response?.status === 409) {
        setHasExistingAccount(true);
        setStep(1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #09090b' }} // brand-lint-ignore: dark-only public page; this pre-existing literal is pinned by the data-theme attribute on this tag
        data-theme="dark"
      >
        <div className="flex items-center justify-center gap-3 mb-10">
          <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
          <span className="text-lg font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Hans</span>
        </div>
        <div className="flex items-end justify-center gap-[3px] h-16">
          {[28,42,20,50,35,55,25,45,30,52,22,48,38,44,26,40].map((h, i) => (
            <div key={i} className="soundwave-bar" style={{ height: `${h}px`, animationDelay: `${i * 0.07}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #09090b' }} // brand-lint-ignore: dark-only public page; this pre-existing literal is pinned by the data-theme attribute on this tag
        data-theme="dark"
      >
        <div>
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Invite Not Valid
          </h1>
          <p className="text-zinc-400 text-sm mb-6">{error}</p>
          <Link to="/student/login" className="text-[#f4cf08] text-sm hover:underline">
            Already have an account? Sign in
          </Link>
        </div>
      </div>
    );
  }

  // Under-13 students don't get their own portal login — the parent receives
  // lesson summaries by email. Deflect with an explanation rather than a form
  // that the server would reject anyway (backend backstops this too).
  if (!portalAllowed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #09090b' }} // brand-lint-ignore: dark-only public page; this pre-existing literal is pinned by the data-theme attribute on this tag
        data-theme="dark"
      >
        <div className="max-w-sm">
          <div className="w-16 h-16 bg-[#f4cf08]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-[#f4cf08]" />
          </div>
          <h1 className="text-xl font-light mb-3" style={{ fontFamily: "Outfit, sans-serif", color: "var(--text-primary)" }}>
            No sign-in needed
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            The student portal isn't available for students under 13. Your
            teacher shares lesson summaries and progress with your parent or
            guardian by email instead.
          </p>
        </div>
      </div>
    );
  }

  // Email is bound to the invite's roster record server-side, so show it read-only.
  const emailLocked = Boolean(inviteInfo.student_email);
  const isTeen = ageBand === "13_17";

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #09090b' }}
      data-theme="dark"
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
            <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Hans</span>
          </div>
          <h1 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {step === 2
              ? "Your musical profile"
              : hasExistingAccount
              ? "Welcome back!"
              : "You've been invited!"}
          </h1>
          <p className="text-zinc-400 text-sm">
            {step === 2 ? (
              "Help your instructor tailor lessons just for you."
            ) : hasExistingAccount ? (
              <>You already have a Hans account. Enter your password to link{" "}
              <span className="text-white font-medium">{inviteInfo.teacher_name}</span>.</>
            ) : (
              <><span className="text-white font-medium">{inviteInfo.teacher_name}</span> has invited you to join the Hans student portal.</>
            )}
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                s === step ? "w-8 bg-[#f4cf08]" : s < step ? "w-4 bg-[#f4cf08]/50" : "w-4 bg-zinc-700"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.form
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              onSubmit={handleStep1}
              className="space-y-4"
            >
              <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 space-y-4">
                {!hasExistingAccount && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs uppercase tracking-wider">First Name</Label>
                      <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="mt-1 bg-zinc-800 border-zinc-700 text-white" placeholder="First name" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs uppercase tracking-wider">Last Name</Label>
                      <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700 text-white" placeholder="Last name" />
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    readOnly={emailLocked}
                    aria-readonly={emailLocked}
                    className={`mt-1 bg-zinc-800 border-zinc-700 text-white${emailLocked ? " opacity-70 cursor-not-allowed" : ""}`}
                    placeholder="your@email.com"
                  />
                </div>
                {hasExistingAccount ? (
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password</Label>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="current-password" className="mt-1 bg-zinc-800 border-zinc-700 text-white pr-10" placeholder="Your Hans password" />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                        className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-zinc-400 hover:text-white transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-zinc-500 text-xs mt-1" data-testid="existing-account-hint">
                      This email already has a Hans account — enter its password to link.{" "}
                      <Link to="/student/forgot-password" className="text-[#f4cf08] hover:underline">Forgot it?</Link>
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password</Label>
                      <div className="relative">
                        <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" className="mt-1 bg-zinc-800 border-zinc-700 text-white pr-10" placeholder="At least 8 characters" />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          aria-pressed={showPassword}
                          className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-zinc-400 hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-zinc-500 text-xs mt-1" data-testid="password-hint">
                        Use at least 8 characters, with a mix of letters, numbers, and symbols.
                      </p>
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs uppercase tracking-wider">Confirm Password</Label>
                      <div className="relative">
                        <Input type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} autoComplete="new-password" className="mt-1 bg-zinc-800 border-zinc-700 text-white pr-10" placeholder="Confirm your password" />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          aria-pressed={showConfirmPassword}
                          className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-zinc-400 hover:text-white transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {/* Live "passwords match" feedback as the user types, before submit. */}
                      {confirmPassword && (
                        password === confirmPassword ? (
                          <p className="text-green-400 text-xs mt-1" data-testid="confirm-password-match">Passwords match</p>
                        ) : (
                          <p className="text-red-400 text-xs mt-1" data-testid="confirm-password-error">Passwords don't match</p>
                        )
                      )}
                    </div>
                  </>
                )}
              </div>

              <label className="flex items-start gap-3 cursor-pointer px-1">
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  className="mt-0.5 accent-[#f4cf08]"
                  data-testid="tos-checkbox"
                />
                <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener" className="text-[#f4cf08] hover:underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" rel="noopener" className="text-[#f4cf08] hover:underline">
                    Privacy Policy
                  </Link>
                  {isTeen && (
                    <>. If I'm under 18, a parent or guardian has reviewed and agreed to them.</>
                  )}
                </span>
              </label>

              <Button type="submit" className="w-full bg-[#f4cf08] text-black font-medium rounded-full py-3 hover:scale-105 active:scale-95 transition-transform">
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.form>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 space-y-6">
                {/* Favorite Artist */}
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-1.5 mb-3">
                    <Star className="w-3 h-3" /> Favorite Artist
                  </Label>
                  <Input
                    value={favoriteArtist}
                    onChange={(e) => setFavoriteArtist(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="e.g. Chopin, Miles Davis, Taylor Swift..."
                  />
                </div>

                {/* Musical Interests */}
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-1.5 mb-3">
                    <Music className="w-3 h-3" /> Musical Interests
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {INTEREST_OPTIONS.map((interest) => {
                      const selected = interests.includes(interest);
                      return (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => toggleInterest(interest)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            selected
                              ? "bg-[#f4cf08] text-black"
                              : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white"
                          }`}
                        >
                          {selected && <X className="w-2.5 h-2.5 inline mr-1" />}
                          {interest}
                        </button>
                      );
                    })}
                  </div>
                  {interests.length > 0 && (
                    <p className="text-zinc-600 text-xs mt-2">{interests.length} selected</p>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-[#f4cf08] text-black font-medium rounded-full py-3 hover:scale-105 active:scale-95 transition-transform"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <GraduationCap className="w-4 h-4 mr-2" />}
                {submitting
                  ? hasExistingAccount ? "Linking..." : "Creating Account..."
                  : hasExistingAccount ? "Link My Account" : "Join Student Portal"}
              </Button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-zinc-500 text-sm hover:text-zinc-300 transition-colors py-1"
              >
                ← Back
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="text-center text-zinc-500 text-xs mt-6">
          Already have an account?{" "}
          <Link to="/student/login" className="text-[#f4cf08] hover:underline">Sign in</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default StudentInvitePage;
